import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import { FiRefreshCw, FiSearch, FiEye, FiAlertCircle, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import { FaAdjust } from 'react-icons/fa';

interface ClaimStatus {
        id: string;
        claim_number: string;
        invoice_number: string;
        patient_name: string;
        patient_medicare_number?: string;
        service_date: string;
        submission_date: string;
        item_numbers: string[];
        total_claim_amount: number;
        payment_amount?: number;
        status: 'pending' | 'assessed' | 'paid' | 'rejected' | 'under_review';
        status_message?: string;
        payment_date?: string;
        rejection_reason?: string;
        provider_name: string;
        last_updated: string;
}

const EclipseClaimStatus: React.FC = () => {
        const { token } = useAuth();
        const [claims, setClaims] = useState<ClaimStatus[]>([]);
        const [loading, setLoading] = useState(true);
        const [refreshing, setRefreshing] = useState(false);
        const [searchTerm, setSearchTerm] = useState('');
        const [statusFilter, setStatusFilter] = useState<string>('all');
        const [dateFrom, setDateFrom] = useState('');
        const [dateTo, setDateTo] = useState('');

        useEffect(() => {
                if (token) {
                        fetchClaims();
                }
        }, [token, statusFilter, dateFrom, dateTo]);

        const fetchClaims = async () => {
                try {
                        setLoading(true);
                        const params = new URLSearchParams();
                        if (statusFilter !== 'all') params.append('status', statusFilter);
                        if (dateFrom) params.append('date_from', dateFrom);
                        if (dateTo) params.append('date_to', dateTo);
                        if (searchTerm) params.append('search', searchTerm);

                        const url = `/billing/eclipse-claims/?${params.toString()}`;
                        const response = await apiCall(url, { method: 'GET' }, token!);

                        if (response.ok) {
                                const data = await response.json();
                                setClaims(data.results || data);
                        }
                } catch (error) {
                        console.error('Error fetching claims:', error);
                } finally {
                        setLoading(false);
                }
        };

        const handleRefreshStatus = async (claimId?: string) => {
                try {
                        setRefreshing(true);
                        const url = claimId
                                ? `/billing/eclipse-claims/${claimId}/refresh/`
                                : '/billing/eclipse-claims/refresh-all/';

                        const response = await apiCall(url, { method: 'POST' }, token!);

                        if (response.ok) {
                                await fetchClaims();
                                alert(claimId ? 'Claim status updated' : 'All claim statuses updated');
                        }
                } catch (error) {
                        console.error('Error refreshing status:', error);
                        alert('Failed to refresh claim status');
                } finally {
                        setRefreshing(false);
                }
        };

        const handleSearch = () => {
                fetchClaims();
        };

        const getStatusIcon = (status: ClaimStatus['status']) => {
                switch (status) {
                        case 'paid':
                                return <FiCheckCircle className="text-success" />;
                        case 'assessed':
                                return <FiCheckCircle className="text-info" />;
                        case 'rejected':
                                return <FiXCircle className="text-error" />;
                        case 'under_review':
                                return <FiAlertCircle className="text-warning" />;
                        default:
                                return <FiClock className="text-base-content opacity-50" />;
                }
        };

        const getStatusBadgeClass = (status: ClaimStatus['status']) => {
                switch (status) {
                        case 'paid':
                                return 'badge-success';
                        case 'assessed':
                                return 'badge-info';
                        case 'rejected':
                                return 'badge-error';
                        case 'under_review':
                                return 'badge-warning';
                        default:
                                return 'badge-ghost';
                }
        };

        const getStats = () => {
                const total = claims.length;
                const pending = claims.filter((c) => c.status === 'pending').length;
                const paid = claims.filter((c) => c.status === 'paid').length;
                const rejected = claims.filter((c) => c.status === 'rejected').length;
                const totalClaimed = claims.reduce((sum, c) => sum + c.total_claim_amount, 0);
                const totalPaid = claims.reduce((sum, c) => sum + (c.payment_amount || 0), 0);

                return { total, pending, paid, rejected, totalClaimed, totalPaid };
        };

        const stats = getStats();

        return (
                <div className="p-6">
                        {/* Header with Stats */}
                        <div className="flex justify-between items-center mb-6">
                                <div>
                                        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                                <FaAdjust className="text-primary" />
                                                Eclipse Claim Status
                                        </h1>
                                </div>
                                
                                <div className="flex gap-4">
                                        <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                <FiClock className="text-primary text-2xl" />
                                                <span className="text-xs">Total Claims:</span>
                                                <span className="text-lg font-bold">{stats.total}</span>
                                        </div>
                                        <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                <FiAlertCircle className="text-warning text-2xl" />
                                                <span className="text-xs">Pending:</span>
                                                <span className="text-lg font-bold text-warning">{stats.pending}</span>
                                        </div>
                                        <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                <FiCheckCircle className="text-success text-2xl" />
                                                <span className="text-xs">Paid:</span>
                                                <span className="text-lg font-bold text-success">{stats.paid}</span>
                                                <span className="text-xs">(${stats.totalPaid.toFixed(2)})</span>
                                        </div>
                                        <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                <FiXCircle className="text-error text-2xl" />
                                                <span className="text-xs">Rejected:</span>
                                                <span className="text-lg font-bold text-error">{stats.rejected}</span>
                                        </div>
                                </div>
                                
                                <button
                                        onClick={() => handleRefreshStatus()}
                                        disabled={refreshing}
                                        className="btn btn-primary gap-2"
                                >
                                        <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
                                        {refreshing ? 'Refreshing...' : 'Refresh All'}
                                </button>
                        </div>

                        {/* Filters */}
                        <div className="bg-base-200 rounded-lg p-4 mb-6">
                                <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-2 flex-1">
                                                <span className="text-sm font-medium whitespace-nowrap">Search</span>
                                                <div className="relative flex-1">
                                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content opacity-50" />
                                                        <input
                                                                type="text"
                                                                placeholder="Patient, claim #..."
                                                                className="input input-bordered w-full pl-10"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                        />
                                                </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium whitespace-nowrap">Status</span>
                                                <select
                                                        className="select select-bordered"
                                                        value={statusFilter}
                                                        onChange={(e) => setStatusFilter(e.target.value)}
                                                >
                                                        <option value="all">All Status</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="assessed">Assessed</option>
                                                        <option value="paid">Paid</option>
                                                        <option value="rejected">Rejected</option>
                                                        <option value="under_review">Under Review</option>
                                                </select>
                                        </div>

                                        <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium whitespace-nowrap">Date From</span>
                                                <input
                                                        type="date"
                                                        className="input input-bordered"
                                                        value={dateFrom}
                                                        onChange={(e) => setDateFrom(e.target.value)}
                                                />
                                        </div>

                                        <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium whitespace-nowrap">Date To</span>
                                                <input
                                                        type="date"
                                                        className="input input-bordered"
                                                        value={dateTo}
                                                        onChange={(e) => setDateTo(e.target.value)}
                                                />
                                        </div>
                                </div>
                        </div>

                        {/* Table */}
                        <div className="bg-base-100 rounded-lg shadow overflow-hidden">
                                {loading ? (
                                        <div className="flex justify-center items-center p-12">
                                                <div className="loading loading-spinner loading-lg"></div>
                                        </div>
                                ) : claims.length === 0 ? (
                                        <div className="text-center p-12">
                                                <p className="text-lg opacity-60">No claims found</p>
                                                <p className="text-sm opacity-50 mt-2">
                                                        Claims will appear here once submitted to Medicare
                                                </p>
                                        </div>
                                ) : (
                                        <div className="overflow-x-auto">
                                                <table className="table w-full">
                                                        <thead>
                                                                <tr>
                                                                        <th>Status</th>
                                                                        <th>Claim #</th>
                                                                        <th>Invoice #</th>
                                                                        <th>Patient</th>
                                                                        <th>Medicare #</th>
                                                                        <th>Provider</th>
                                                                        <th>Service Date</th>
                                                                        <th>Submitted</th>
                                                                        <th>Items</th>
                                                                        <th className="text-right">Claimed</th>
                                                                        <th className="text-right">Paid</th>
                                                                        <th>Last Updated</th>
                                                                        <th>Actions</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {claims.map((claim) => (
                                                                        <tr key={claim.id} className="hover">
                                                                                <td>
                                                                                        <div className="flex items-center gap-2">
                                                                                                {getStatusIcon(claim.status)}
                                                                                                <span
                                                                                                        className={`badge ${getStatusBadgeClass(
                                                                                                                claim.status
                                                                                                        )}`}
                                                                                                >
                                                                                                        {claim.status}
                                                                                                </span>
                                                                                        </div>
                                                                                </td>
                                                                                <td className="font-medium">{claim.claim_number}</td>
                                                                                <td>
                                                                                        <a
                                                                                                href={`/billing/invoices/${claim.invoice_number}`}
                                                                                                className="link link-primary"
                                                                                        >
                                                                                                {claim.invoice_number}
                                                                                        </a>
                                                                                </td>
                                                                                <td>{claim.patient_name}</td>
                                                                                <td className="font-mono text-sm">
                                                                                        {claim.patient_medicare_number || 'N/A'}
                                                                                </td>
                                                                                <td>{claim.provider_name}</td>
                                                                                <td>{claim.service_date}</td>
                                                                                <td>{claim.submission_date}</td>
                                                                                <td>
                                                                                        <div className="badge badge-sm">
                                                                                                {claim.item_numbers.join(', ')}
                                                                                        </div>
                                                                                </td>
                                                                                <td className="text-right font-medium">
                                                                                        ${claim.total_claim_amount.toFixed(2)}
                                                                                </td>
                                                                                <td className="text-right">
                                                                                        {claim.payment_amount ? (
                                                                                                <span className="text-success font-medium">
                                                                                                        ${claim.payment_amount.toFixed(2)}
                                                                                                </span>
                                                                                        ) : (
                                                                                                <span className="opacity-50">-</span>
                                                                                        )}
                                                                                </td>
                                                                                <td className="text-sm opacity-60">{claim.last_updated}</td>
                                                                                <td>
                                                                                        <div className="flex gap-2">
                                                                                                <button
                                                                                                        onClick={() => handleRefreshStatus(claim.id)}
                                                                                                        disabled={refreshing}
                                                                                                        className="btn btn-ghost btn-xs"
                                                                                                        title="Refresh Status"
                                                                                                >
                                                                                                        <FiRefreshCw
                                                                                                                className={refreshing ? 'animate-spin' : ''}
                                                                                                        />
                                                                                                </button>
                                                                                                <button
                                                                                                        className="btn btn-ghost btn-xs"
                                                                                                        title="View Details"
                                                                                                >
                                                                                                        <FiEye />
                                                                                                </button>
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        </div>
                                )}
                        </div>

                        {/* Info Alert */}
                        <div className="alert alert-info mt-6">
                                <FiAlertCircle />
                                <div>
                                        <h3 className="font-bold">Eclipse Integration</h3>
                                        <div className="text-xs">
                                                Claim statuses are automatically updated from Eclipse Medicare systems. Use the Refresh button to
                                                manually check for updates. Typical processing time is 24-48 hours.
                                        </div>
                                </div>
                        </div>
                </div>
        );
};

export default EclipseClaimStatus;
